import React, { memo, useCallback, useMemo } from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { useSelector } from 'react-redux';
import { getCurrentEmployer } from '../../../selectors/employers';
import { ActionButton } from '../../components';
import * as ASSETS from '../../../constants/assets';
import { __WEB__, COLOR, FONT, THEME } from '../../../constants/styles';
import moment from 'moment';
import {
  EVENT_TYPE_CMR,
  EVENT_TYPE_CMR_FLY,
  EVENT_TYPE_READINESS,
} from '../../../constants/eventTypes';
import Overrides from './scheduled_overrides';

const RevenueActionButton = memo(({ disabled, eventId, onPress }) => {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Edit CMR attached services"
      disabled={disabled}
      onPress={onPress}
      style={styles.revenueActionButton}
      testID={`cmr-attached-services-${eventId}`}
    >
      <Text style={[styles.revenueActionText, disabled ? styles.revenueActionTextDisabled : null]}>
        $
      </Text>
    </Pressable>
  );
});

const getAppointmentType = (eventTypeName) => {
  return eventTypeName?.split(' Appointment')[0] || eventTypeName || null;
};

const buildRequestInfo = (event) => {
  if (event.caseId) return null;

  return {
    details: {
      full_name: null,
      phone: null,
      squadron: null,
      rank: null,
    },
    email: null,
    appointment_time: event.origStartDate,
    appointment_type: getAppointmentType(event.eventTypeName),
    unavailable: event.unavailable,
    allDay: event.allDay,
  };
};

const buildDisplayName = (event) => {
  return `${event.rank || ''} ${event.caseName || ''}`.trim();
};

const compareEventsByStartDate = (firstEvent, secondEvent) => {
  const firstTimestamp = Date.parse(firstEvent?.startDate);
  const secondTimestamp = Date.parse(secondEvent?.startDate);

  if (!Number.isNaN(firstTimestamp) && !Number.isNaN(secondTimestamp)) {
    if (firstTimestamp === secondTimestamp) return 0;
    return firstTimestamp - secondTimestamp;
  }

  const firstValue = firstEvent?.startDate || '';
  const secondValue = secondEvent?.startDate || '';
  return firstValue.localeCompare(secondValue);
};

const buildSmsInfo = (event, employerId) => {
  if (!event?.eventId || !employerId) return null;

  return {
    ...event,
    employerId,
    full_name: buildDisplayName(event),
    phone: event.phone || null,
  };
};

const buildTimeGroups = (eventsBySelectedDate, employerId) => {
  return Object.keys(eventsBySelectedDate)
    .sort()
    .map((time) => ({
      time,
      rows: [...(eventsBySelectedDate[time] || [])].sort(compareEventsByStartDate).map((event) => ({
        key: event.eventId,
        event,
        requestInfo: buildRequestInfo(event),
        smsInfo: buildSmsInfo(event, employerId),
        displayName: buildDisplayName(event),
      })),
    }));
};

const PHAEventRow = memo(
  ({
    event,
    requestInfo,
    smsInfo,
    displayName,
    onCancelPress,
    onInfoPress,
    onContactPress,
    onRevenuePress,
    isCMREvent,
    canEdit,
  }) => {
    const handleInfoPress = useCallback(() => {
      onInfoPress(requestInfo, event);
    }, [onInfoPress, requestInfo, event]);

    const handleContactPress = useCallback(() => {
      if (!smsInfo || !onContactPress) return;
      onContactPress(smsInfo);
    }, [onContactPress, smsInfo]);

    const handleCancelPress = useCallback(() => {
      onCancelPress(event);
    }, [onCancelPress, event]);

    const handleRevenuePress = useCallback(() => {
      onRevenuePress(event);
    }, [event, onRevenuePress]);

    const isDisabled = !canEdit;
    const isInPast = moment(event.startDate).isBefore(moment());
    const isCancelDisabled = isDisabled || isInPast;
    const shouldShowRevenueAction = isCMREvent && !!event.eventId && !!onRevenuePress;

    return (
      <View style={styles.eventRow}>
        <View
          style={[
            styles.eventActions,
            shouldShowRevenueAction ? styles.eventActionsWithRevenue : null,
          ]}
        >
          <View style={THEME.basicRow}>
            {!!smsInfo && (
              <ActionButton
                source={ASSETS.message}
                disabled={isDisabled}
                onPress={handleContactPress}
                extraStyle={{ width: 21, height: 21, top: 1.5 }}
              />
            )}
            <ActionButton source={ASSETS.info} disabled={isDisabled} onPress={handleInfoPress} />
            {shouldShowRevenueAction && (
              <RevenueActionButton
                disabled={isDisabled}
                eventId={event.eventId}
                onPress={handleRevenuePress}
              />
            )}
            <ActionButton
              source={ASSETS.reject}
              disabled={isCancelDisabled}
              onPress={handleCancelPress}
            />
          </View>
        </View>
        <View style={styles.eventDetails}>
          <Text
            style={[
              styles.eventRowNameLabel,
              event.unavailable ? styles.eventRowNameLabelUnavailable : null,
            ]}
          >
            {displayName}
          </Text>
        </View>
      </View>
    );
  }
);

export default memo(function ScheduledAppointments(props) {
  const employerId = useSelector(getCurrentEmployer)?.employerId;
  const selectedDateKey = useMemo(
    () => moment(props.selectedDate).format('YYYY-MM-DD'),
    [props.selectedDate]
  );

  const eventsBySelectedDate = useMemo(
    () => props.eventsByTime?.[selectedDateKey] || {},
    [props.eventsByTime, selectedDateKey]
  );

  const timeGroups = useMemo(
    () => buildTimeGroups(eventsBySelectedDate, employerId),
    [eventsBySelectedDate, employerId]
  );

  const shouldShowOverrides = props.eventType !== EVENT_TYPE_READINESS;
  const isCMREvent = [EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(props.eventType);

  return (
    <>
      {timeGroups.map((timeGroup) => {
        return (
          <View key={timeGroup.time} style={styles.timeSection}>
            <View style={[styles.scheduledTextBubble, THEME.textBubbleSelected]}>
              <Text style={styles.textBubbleText}>{timeGroup.time}</Text>
            </View>
            <View style={styles.timeEventsContainer}>
              {timeGroup.rows.map((row) => (
                <PHAEventRow
                  key={row.key}
                  event={row.event}
                  requestInfo={row.requestInfo}
                  smsInfo={row.smsInfo}
                  displayName={row.displayName}
                  onCancelPress={props.onCancelPress}
                  onInfoPress={props.onInfoPress}
                  onContactPress={props.onContactPress}
                  onRevenuePress={props.onRevenuePress}
                  isCMREvent={isCMREvent}
                  canEdit={props.canEdit}
                />
              ))}
            </View>
          </View>
        );
      })}
      {shouldShowOverrides && (
        <Overrides
          selectedDate={props.selectedDate}
          overrides={props.overrides}
          canEdit={props.canEdit}
        />
      )}
    </>
  );
});

const styles = StyleSheet.create({
  eventRow: __WEB__
    ? {
        flex: 1,
        ...THEME.basicRow,
      }
    : {
        ...THEME.basicRow,
      },
  eventActions: {
    width: __WEB__ ? 120 : 110,
  },
  eventActionsWithRevenue: {
    width: __WEB__ ? 155 : 145,
  },
  revenueActionButton: {
    paddingVertical: 10,
    justifyContent: 'center',
  },
  revenueActionText: {
    ...FONT.semibold,
    color: COLOR.callToAction,
    width: 20,
    height: 20,
    marginHorizontal: 8,
    fontSize: 20,
    lineHeight: 20,
    textAlign: 'center',
  },
  revenueActionTextDisabled: {
    color: COLOR.textDisabled,
  },
  eventDetails: {
    flex: 1,
  },
  eventRowNameLabel: {
    ...FONT.semibold,
    color: COLOR.text,
    fontSize: 16,
  },
  eventRowNameLabelUnavailable: {
    color: COLOR.textLight,
    fontSize: 14,
  },
  scheduledTextBubble: {
    ...THEME.textBubble,
    width: 120,
    height: 50,
    borderRadius: 30,
    justifyContent: 'center',
    marginRight: __WEB__ ? 30 : 10,
  },
  textBubbleText: {
    ...THEME.textBubbleText,
    fontSize: 20,
    letterSpacing: 1,
  },
  timeSection: {
    flexDirection: __WEB__ ? 'row' : 'column',
    alignItems: __WEB__ ? 'center' : 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: COLOR.altAccent,
    paddingLeft: 20,
    paddingBottom: 20,
    paddingTop: 10,
  },
  timeEventsContainer: {
    width: '100%',
  },
});
